/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React from 'react';
import { Plan, PlanTask, PlanTaskStatus } from '@shared/types/plan';
import { 
  CheckCircle2, 
  Circle, 
  Loader2, 
  ListTodo, 
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@shared/components/ui/collapsible';
import { Badge } from '@shared/components/ui/badge';

interface ChatPlanDisplayProps {
  plan: Plan;
  className?: string;
  /** Display mode: 'inline' for in-chat display, 'header' for collapsible header section */
  mode?: 'inline' | 'header';
  /** Whether the header section is expanded by default (only for 'header' mode) */
  defaultExpanded?: boolean;
}

const getStatusIcon = (status: PlanTaskStatus) => {
  switch (status) {
    case 'completed':
      return <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />;
    case 'in_progress':
      return <Loader2 className="h-4 w-4 text-blue-600 animate-spin flex-shrink-0" />;
    case 'not_started':
    default:
      return <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
  }
};

const getStatusColor = (status: PlanTaskStatus) => {
  switch (status) {
    case 'completed':
      return 'text-green-700 bg-green-50 border-green-200';
    case 'in_progress':
      return 'text-blue-700 bg-blue-50 border-blue-200';
    case 'not_started':
    default:
      return 'text-gray-700 bg-gray-50 border-gray-200';
  }
};

const getStatusLabel = (status: PlanTaskStatus) => {
  switch (status) {
    case 'completed':
      return 'Done';
    case 'in_progress':
      return 'In Progress';
    case 'not_started':
    default:
      return 'Pending';
  }
};

function TaskItem({ task, index }: { task: PlanTask; index: number }) {
  return (
    <div 
      className={cn(
        "flex items-start gap-3 p-2 border text-sm transition-all duration-200",
        getStatusColor(task.status),
        task.status === 'completed' && "opacity-75"
      )}
    >
      <div className="mt-0.5">
        {getStatusIcon(task.status)}
      </div>
      <div className="flex-1 min-w-0">
        <div className={cn(
          task.status === 'completed' && "line-through"
        )}>
          {task.description}
        </div>
      </div>
    </div>
  );
}

function PlanSummary({ plan }: { plan: Plan }) {
  const total = plan.tasks.length;
  const completed = plan.tasks.filter(t => t.status === 'completed').length;
  const inProgress = plan.tasks.filter(t => t.status === 'in_progress').length;
  const pending = plan.tasks.filter(t => t.status === 'not_started').length;
  
  const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;
  
  return (
    <div className="flex items-center gap-3 text-xs text-muted-foreground">
      <span className="font-medium">{progressPercent}% complete</span>
      <div className="flex items-center gap-2">
        {completed > 0 && (
          <Badge variant="outline" className="text-green-700 bg-green-50 border-green-200 text-xs py-0 px-1.5">
            {completed} done
          </Badge>
        )}
        {inProgress > 0 && (
          <Badge variant="outline" className="text-blue-700 bg-blue-50 border-blue-200 text-xs py-0 px-1.5">
            {inProgress} active
          </Badge>
        )}
        {pending > 0 && (
          <Badge variant="outline" className="text-gray-700 bg-gray-50 border-gray-200 text-xs py-0 px-1.5">
            {pending} pending
          </Badge>
        )}
      </div>
    </div>
  );
}

/**
 * Inline plan display for showing within chat messages
 */
function InlinePlanDisplay({ plan, className }: { plan: Plan; className?: string }) {
  if (!plan.tasks || plan.tasks.length === 0) {
    return null;
  }

  return (
    <div className={cn("w-full mt-3 overflow-hidden", className)}>
      <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
        <ListTodo className="h-3.5 w-3.5" />
        <span>Plan ({plan.tasks.length} tasks)</span>
      </div>
      
      <div className="space-y-2">
        {plan.tasks.map((task, index) => (
          <TaskItem key={task.id} task={task} index={index} />
        ))}
      </div>
      
      <div className="mt-3 pt-2 border-t border-border/50">
        <PlanSummary plan={plan} />
      </div>
    </div>
  );
}

/**
 * Header plan display as a collapsible section at the top of the chat
 */
function HeaderPlanDisplay({ plan, className, defaultExpanded = true }: { 
  plan: Plan; 
  className?: string;
  defaultExpanded?: boolean;
}) {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  
  if (!plan.tasks || plan.tasks.length === 0) {
    return null;
  }

  const total = plan.tasks.length;
  const completed = plan.tasks.filter(t => t.status === 'completed').length;
  const progressPercent = total > 0 ? Math.round((completed / total) * 100) : 0;

  return (
    <Collapsible 
      open={isExpanded} 
      onOpenChange={setIsExpanded}
      className={cn(
        "border-b border-border bg-muted/30",
        className
      )}
    >
      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2">
          <ListTodo className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium text-sm">Plan</span>
          <Badge variant="outline" className="text-xs py-0 px-1.5">
            {completed}/{total}
          </Badge>
          {progressPercent === 100 && (
            <Badge className="text-xs py-0 px-1.5 bg-green-600">
              Complete
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Progress bar */}
          <div className="w-24 h-1.5 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-600 transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="px-3 pb-3">
        <div className="space-y-2">
          {plan.tasks.map((task, index) => (
            <TaskItem key={task.id} task={task} index={index} />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/**
 * ChatPlanDisplay - Display a chat session's plan
 * 
 * Supports two display modes:
 * - 'inline': For displaying within chat messages (similar to EntityLinksDisplay)
 * - 'header': For displaying as a collapsible section at the top of the chat
 */
export function ChatPlanDisplay({ 
  plan, 
  className, 
  mode = 'inline',
  defaultExpanded = true 
}: ChatPlanDisplayProps) {
  if (!plan || !plan.tasks || plan.tasks.length === 0) {
    return null;
  }

  if (mode === 'header') {
    return <HeaderPlanDisplay plan={plan} className={className} defaultExpanded={defaultExpanded} />;
  }

  return <InlinePlanDisplay plan={plan} className={className} />;
}

export default ChatPlanDisplay;
